import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import classNames from "classnames";
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getPropRenderThumbnailCacheKey,
  setCachedPropRenderThumbnail,
  selectCachedPropRenderThumbnails,
} from "../../../app/slices/PropLibrarySlice";
import { selectToken } from "../../../app/slices/AuthenticationSlice";
import { selectCurrentOrganization } from "../../../app/slices/UserSlice";
import type { ClickableOverlay } from "../../../dto/PropMetadata";
import { PropType } from "../../../models/Prop";
import type { IPropLabelQuizAnswer } from "../../../models/interactions/PropLabelQuiz";
import { PropService } from "../../../services/PropService";

export type PropLabelQuizInspectDefinitionOption = {
  id?: string;
  name: string;
  description: string;
};

export type PropLabelQuizInspectPropOption = {
  id: string;
  propId: string;
  propType: PropType;
  name: string;
  clickableTags: string[];
  inspectDefinitions: PropLabelQuizInspectDefinitionOption[];
  clickableOverlaysByInspectId?: Record<string, ClickableOverlay[]>;
};

export type PropLabelQuizInspectPreviewItem = {
  id: string;
  displayName: string;
  overlays: ClickableOverlay[];
};

type PropLabelQuizInspectPickerProps = {
  answer: IPropLabelQuizAnswer;
  selectedProp?: PropLabelQuizInspectPropOption;
  frameControls?: ReactNode;
  onSelectOverlay: (inspectDefinitionId: string, clickableName: string) => void;
};

export type ContainedImageBounds = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type PropLabelQuizAttachedLabelDot = {
  id: string;
  label: string;
  left: number;
  top: number;
};

export type PropLabelQuizAttachedLabelPlacement = {
  left: number;
  top: number;
  width: number;
  height: number;
  connectorLeft: number;
  connectorTop: number;
  connectorWidth: number;
  connectorRotation: number;
};

export const getContainedImageBounds = (
  frameWidth: number,
  frameHeight: number,
  naturalWidth: number,
  naturalHeight: number,
): ContainedImageBounds => {
  if (
    frameWidth <= 0 ||
    frameHeight <= 0 ||
    naturalWidth <= 0 ||
    naturalHeight <= 0
  ) {
    return {
      left: 0,
      top: 0,
      width: frameWidth,
      height: frameHeight,
    };
  }

  const frameRatio = frameWidth / frameHeight;
  const imageRatio = naturalWidth / naturalHeight;

  if (imageRatio > frameRatio) {
    const width = frameWidth;
    const height = frameWidth / imageRatio;

    return {
      left: 0,
      top: (frameHeight - height) / 2,
      width,
      height,
    };
  }

  const height = frameHeight;
  const width = frameHeight * imageRatio;

  return {
    left: (frameWidth - width) / 2,
    top: 0,
    width,
    height,
  };
};

export const getStableImageStageBounds = (
  frameWidth: number,
  frameHeight: number,
): ContainedImageBounds => {
  if (frameWidth <= 0 || frameHeight <= 0) {
    return {
      left: 0,
      top: 0,
      width: frameWidth,
      height: frameHeight,
    };
  }

  const horizontalInset = frameWidth < 1280 ? 16 : 24;
  const topReserve = 18;
  const bottomReserve = frameHeight < 520 ? 68 : 76;
  const availableWidth = Math.max(180, frameWidth - horizontalInset * 2);
  const availableHeight = Math.max(
    180,
    frameHeight - topReserve - bottomReserve,
  );

  return {
    left: horizontalInset,
    top: topReserve,
    width: availableWidth,
    height: availableHeight,
  };
};

export const clampNormalizedCoordinate = (value: number) =>
  Math.min(1, Math.max(0, value));

const labelFrameInset = 8;
const labelHeight = 30;
const labelMinWidth = 72;
const labelMaxWidth = 184;
const labelCharacterWidth = 8;
const labelHorizontalPadding = 30;
const dotAvoidRadius = 20;
const attachedLabelGap = 10;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const estimateAttachedLabelWidth = (label: string) =>
  clamp(
    Math.ceil(label.length * labelCharacterWidth + labelHorizontalPadding),
    labelMinWidth,
    labelMaxWidth,
  );

const rectsIntersect = (
  first: ContainedImageBounds,
  second: ContainedImageBounds,
) =>
  first.left < second.left + second.width &&
  first.left + first.width > second.left &&
  first.top < second.top + second.height &&
  first.top + first.height > second.top;

const getDotAvoidRect = (
  dot: PropLabelQuizAttachedLabelDot,
): ContainedImageBounds => ({
  left: dot.left - dotAvoidRadius,
  top: dot.top - dotAvoidRadius,
  width: dotAvoidRadius * 2,
  height: dotAvoidRadius * 2,
});

const getClampedLabelRect = (
  left: number,
  top: number,
  width: number,
  height: number,
  frameBounds: ContainedImageBounds,
): ContainedImageBounds | undefined => {
  const minLeft = frameBounds.left + labelFrameInset;
  const minTop = frameBounds.top + labelFrameInset;
  const maxLeft = frameBounds.left + frameBounds.width - width - labelFrameInset;
  const maxTop = frameBounds.top + frameBounds.height - height - labelFrameInset;

  if (maxLeft < minLeft || maxTop < minTop) return undefined;

  return {
    left: clamp(left, minLeft, maxLeft),
    top: clamp(top, minTop, maxTop),
    width,
    height,
  };
};

const getNearestPointOnRect = (
  dot: PropLabelQuizAttachedLabelDot,
  rect: ContainedImageBounds,
) => ({
  x: clamp(dot.left, rect.left, rect.left + rect.width),
  y: clamp(dot.top, rect.top, rect.top + rect.height),
});

const getAttachedLabelPlacement = (
  selectedDot: PropLabelQuizAttachedLabelDot,
  rect: ContainedImageBounds,
): PropLabelQuizAttachedLabelPlacement => {
  const connectorEnd = getNearestPointOnRect(selectedDot, rect);
  const deltaX = connectorEnd.x - selectedDot.left;
  const deltaY = connectorEnd.y - selectedDot.top;

  return {
    ...rect,
    connectorLeft: selectedDot.left,
    connectorTop: selectedDot.top,
    connectorWidth: Math.hypot(deltaX, deltaY),
    connectorRotation: (Math.atan2(deltaY, deltaX) * 180) / Math.PI,
  };
};

const getCandidateLabelRects = (
  selectedDot: PropLabelQuizAttachedLabelDot,
  width: number,
  height: number,
  frameBounds: ContainedImageBounds,
) => {
  const offset = dotAvoidRadius + attachedLabelGap;
  const rawRects = [
    {
      left: selectedDot.left + offset,
      top: selectedDot.top - height / 2,
    },
    {
      left: selectedDot.left - offset - width,
      top: selectedDot.top - height / 2,
    },
    {
      left: selectedDot.left - width / 2,
      top: selectedDot.top - offset - height,
    },
    {
      left: selectedDot.left - width / 2,
      top: selectedDot.top + offset,
    },
    {
      left: selectedDot.left + offset,
      top: selectedDot.top - offset - height,
    },
    {
      left: selectedDot.left + offset,
      top: selectedDot.top + offset,
    },
    {
      left: selectedDot.left - offset - width,
      top: selectedDot.top - offset - height,
    },
    {
      left: selectedDot.left - offset - width,
      top: selectedDot.top + offset,
    },
  ];

  return rawRects.flatMap((rect) => {
    const clampedRect = getClampedLabelRect(
      rect.left,
      rect.top,
      width,
      height,
      frameBounds,
    );

    return clampedRect ? [clampedRect] : [];
  });
};

export const getPropLabelQuizAttachedLabelPlacement = (
  selectedDot: PropLabelQuizAttachedLabelDot,
  dots: PropLabelQuizAttachedLabelDot[],
  frameBounds: ContainedImageBounds,
): PropLabelQuizAttachedLabelPlacement | undefined => {
  if (frameBounds.width <= 0 || frameBounds.height <= 0) return undefined;

  const width = estimateAttachedLabelWidth(selectedDot.label);
  const height = labelHeight;
  const dotAvoidRects = dots.map(getDotAvoidRect);
  const isCollisionFree = (rect: ContainedImageBounds) =>
    dotAvoidRects.every((dotRect) => !rectsIntersect(rect, dotRect));
  const initialCandidate = getCandidateLabelRects(
    selectedDot,
    width,
    height,
    frameBounds,
  ).find(isCollisionFree);

  if (initialCandidate) {
    return getAttachedLabelPlacement(selectedDot, initialCandidate);
  }

  for (
    let radius = dotAvoidRadius + attachedLabelGap;
    radius <= 280;
    radius += 12
  ) {
    for (let angle = 0; angle < 360; angle += 20) {
      const radians = (angle * Math.PI) / 180;
      const rect = getClampedLabelRect(
        selectedDot.left + Math.cos(radians) * radius - width / 2,
        selectedDot.top + Math.sin(radians) * radius - height / 2,
        width,
        height,
        frameBounds,
      );

      if (rect && isCollisionFree(rect)) {
        return getAttachedLabelPlacement(selectedDot, rect);
      }
    }
  }

  return undefined;
};

export const getInspectDisplayName = (name: string) =>
  name
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

export const getPropLabelQuizInspectDisplayName = (
  prop: PropLabelQuizInspectPropOption | undefined,
  inspectDefinitionId: string | undefined,
) => {
  if (!prop || !inspectDefinitionId) return undefined;
  const inspectDefinition = prop.inspectDefinitions.find(
    (definition) => definition.id === inspectDefinitionId,
  );
  if (!inspectDefinition) return undefined;

  return getInspectDisplayName(inspectDefinition.name);
};

const getInitialPreviewIndex = (
  answer: IPropLabelQuizAnswer,
  items: PropLabelQuizInspectPreviewItem[],
) => {
  if (items.length === 0) return -1;

  if (
    answer.inspectDefinitionId &&
    items.some((item) => item.id === answer.inspectDefinitionId)
  ) {
    return items.findIndex((item) => item.id === answer.inspectDefinitionId);
  }

  const firstMatchingTagIndex = items.findIndex((item) =>
    item.overlays.some((overlay) => overlay.clickableName === answer.tag),
  );

  return firstMatchingTagIndex >= 0 ? firstMatchingTagIndex : 0;
};

export const getPropLabelQuizInspectPreviewItems = (
  selectedProp: PropLabelQuizInspectPropOption | undefined,
): PropLabelQuizInspectPreviewItem[] => {
  if (!selectedProp || selectedProp.propType !== PropType.Default) return [];

  return selectedProp.inspectDefinitions.flatMap((inspectDefinition) => {
    if (!inspectDefinition.id) return [];
    const overlays = (
      selectedProp.clickableOverlaysByInspectId?.[inspectDefinition.id] ?? []
    ).filter((overlay) => Boolean(overlay.clickableName));
    if (overlays.length === 0) return [];

    return [
      {
        id: inspectDefinition.id,
        displayName: getInspectDisplayName(inspectDefinition.name),
        overlays,
      },
    ];
  });
};

export default function PropLabelQuizInspectPicker({
  answer,
  selectedProp,
  frameControls,
  onSelectOverlay,
}: PropLabelQuizInspectPickerProps) {
  const dispatch = useDispatch();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const token = useSelector(selectToken);
  const cachedPropRenderThumbnails = useSelector(
    selectCachedPropRenderThumbnails,
  );
  const cachedPropRenderThumbnailsRef = useRef(cachedPropRenderThumbnails);
  const inFlightThumbnailKeys = useRef(new Set<string>());
  const frameRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const previewResetKeyRef = useRef<string | undefined>();
  const [activePreviewIndex, setActivePreviewIndex] = useState(-1);
  const [imageBounds, setImageBounds] = useState<ContainedImageBounds>({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });
  const [frameBounds, setFrameBounds] = useState<ContainedImageBounds>({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });
  const [imageStageBounds, setImageStageBounds] = useState<ContainedImageBounds>(
    {
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    },
  );
  const [loadedThumbnailKey, setLoadedThumbnailKey] = useState<
    string | undefined
  >();

  useEffect(() => {
    cachedPropRenderThumbnailsRef.current = cachedPropRenderThumbnails;
  }, [cachedPropRenderThumbnails]);

  const updateImageBounds = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;

    setFrameBounds((previousBounds) => {
      const nextBounds = {
        left: 0,
        top: 0,
        width: frame.clientWidth,
        height: frame.clientHeight,
      };

      if (
        previousBounds.width === nextBounds.width &&
        previousBounds.height === nextBounds.height
      ) {
        return previousBounds;
      }

      return nextBounds;
    });

    const stageBounds = getStableImageStageBounds(
      frame.clientWidth,
      frame.clientHeight,
    );

    setImageStageBounds((previousBounds) => {
      if (
        previousBounds.left === stageBounds.left &&
        previousBounds.top === stageBounds.top &&
        previousBounds.width === stageBounds.width &&
        previousBounds.height === stageBounds.height
      ) {
        return previousBounds;
      }

      return stageBounds;
    });

    const image = imageRef.current;
    if (!image) return;

    const nextBounds = getContainedImageBounds(
      stageBounds.width,
      stageBounds.height,
      image.naturalWidth,
      image.naturalHeight,
    );
    const nextFrameBounds = {
      left: stageBounds.left + nextBounds.left,
      top: stageBounds.top + nextBounds.top,
      width: nextBounds.width,
      height: nextBounds.height,
    };

    setImageBounds((previousBounds) => {
      if (
        previousBounds.left === nextFrameBounds.left &&
        previousBounds.top === nextFrameBounds.top &&
        previousBounds.width === nextFrameBounds.width &&
        previousBounds.height === nextFrameBounds.height
      ) {
        return previousBounds;
      }

      return nextFrameBounds;
    });
  }, []);

  const previewItems = useMemo<PropLabelQuizInspectPreviewItem[]>(() => {
    return getPropLabelQuizInspectPreviewItems(selectedProp);
  }, [selectedProp]);
  const hasAnyPreviewOverlay = useMemo(
    () => previewItems.some((item) => item.overlays.length > 0),
    [previewItems],
  );

  const previewSignature = useMemo(
    () => previewItems.map((item) => `${item.id}:${item.displayName}`).join("|"),
    [previewItems],
  );

  const previewResetKey = [
    answer.id,
    answer.inspectDefinitionId ?? "",
    answer.propGuid,
    answer.tag,
    previewSignature,
  ].join("|");

  useEffect(() => {
    setActivePreviewIndex((currentPreviewIndex) => {
      if (previewResetKeyRef.current !== previewResetKey) {
        previewResetKeyRef.current = previewResetKey;
        return getInitialPreviewIndex(answer, previewItems);
      }

      if (
        currentPreviewIndex >= 0 &&
        currentPreviewIndex < previewItems.length
      ) {
        return currentPreviewIndex;
      }

      return getInitialPreviewIndex(answer, previewItems);
    });
  }, [
    answer.id,
    answer.inspectDefinitionId,
    answer.propGuid,
    answer.tag,
    previewResetKey,
    previewSignature,
    previewItems,
  ]);

  useEffect(() => {
    if (!currentOrganization?.id || !token || !selectedProp) return;
    if (selectedProp.propType !== PropType.Default) return;

    let cancelled = false;
    const loadMissingThumbnails = async () => {
      for (const previewItem of previewItems) {
        const cacheKey = getPropRenderThumbnailCacheKey(
          0,
          selectedProp.propId,
          previewItem.id,
        );
        if (cachedPropRenderThumbnailsRef.current[cacheKey]) continue;
        if (inFlightThumbnailKeys.current.has(cacheKey)) continue;

        inFlightThumbnailKeys.current.add(cacheKey);
        const thumbnail = await PropService.GetPropRenderThumbnail(
          currentOrganization.id,
          token,
          0,
          selectedProp.propId,
          previewItem.id,
        );
        inFlightThumbnailKeys.current.delete(cacheKey);

        if (cancelled || !thumbnail?.downloadUri) continue;
        cachedPropRenderThumbnailsRef.current = {
          ...cachedPropRenderThumbnailsRef.current,
          [cacheKey]: thumbnail.downloadUri,
        };
        dispatch(
          setCachedPropRenderThumbnail({
            propType: 0,
            propId: selectedProp.propId,
            renderId: previewItem.id,
            downloadUri: thumbnail.downloadUri,
          }),
        );
      }
    };

    loadMissingThumbnails();

    return () => {
      cancelled = true;
    };
  }, [
    currentOrganization?.id,
    dispatch,
    previewItems,
    previewSignature,
    selectedProp,
    token,
  ]);

  const activePreview =
    activePreviewIndex >= 0 && activePreviewIndex < previewItems.length
      ? previewItems[activePreviewIndex]
      : previewItems[0];
  const activeThumbnailKey =
    selectedProp && activePreview
      ? getPropRenderThumbnailCacheKey(0, selectedProp.propId, activePreview.id)
      : undefined;
  const activeThumbnailUri = activeThumbnailKey
    ? cachedPropRenderThumbnails[activeThumbnailKey]
    : undefined;
  const activeImageReady =
    Boolean(activeThumbnailKey) && loadedThumbnailKey === activeThumbnailKey;
  const hasMatchingSavedOverlay =
    !answer.tag ||
    previewItems.some((item) =>
      item.overlays.some((overlay) => overlay.clickableName === answer.tag),
    );
  const hasSavedInspectPreview =
    Boolean(answer.inspectDefinitionId) &&
    previewItems.some((item) => item.id === answer.inspectDefinitionId);
  const hasStaleInspectContext =
    Boolean(answer.inspectDefinitionId && answer.tag) &&
    hasSavedInspectPreview &&
    !previewItems.some(
      (item) =>
        item.id === answer.inspectDefinitionId &&
        item.overlays.some((overlay) => overlay.clickableName === answer.tag),
    );
  const canRenderVisualImage =
    Boolean(activeThumbnailUri && activePreview) &&
    hasMatchingSavedOverlay &&
    !hasStaleInspectContext;

  useEffect(() => {
    setLoadedThumbnailKey(undefined);
    setImageBounds({
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    });
    updateImageBounds();

    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateImageBounds);
    observer.observe(frame);

    return () => observer.disconnect();
  }, [
    activePreview?.id,
    activePreviewIndex,
    activeThumbnailKey,
    activeThumbnailUri,
    updateImageBounds,
  ]);

  const cyclePreview = (direction: -1 | 1) => {
    if (previewItems.length < 2) return;

    setActivePreviewIndex((currentPreviewIndex) => {
      const currentIndex =
        currentPreviewIndex >= 0 && currentPreviewIndex < previewItems.length
          ? currentPreviewIndex
          : 0;
      const normalizedIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex =
        (normalizedIndex + direction + previewItems.length) %
        previewItems.length;

      return nextIndex;
    });
  };

  const fallbackLabel = () => {
    if (!selectedProp) return "Select a Prop first";
    if (selectedProp.propType !== PropType.Default) {
      return "Visual picker is available for default Props";
    }
    if (previewItems.length === 0 || !hasAnyPreviewOverlay) {
      return "No inspect overlays available";
    }
    if (!activeThumbnailUri) return "Inspect preview unavailable";
    if (!hasMatchingSavedOverlay || hasStaleInspectContext) {
      return "Saved Clickable tag is not on this inspect preview";
    }
    return "Select a Clickable tag";
  };
  const measuredImageBounds =
    imageBounds.width > 0 && imageBounds.height > 0
      ? imageBounds
      : imageStageBounds;
  const frameControlsStyle = {
    "--proplabelquiz-image-left": `${imageStageBounds.left}px`,
    "--proplabelquiz-image-right": `${
      imageStageBounds.left + imageStageBounds.width
    }px`,
    "--proplabelquiz-image-center": `${
      measuredImageBounds.left + measuredImageBounds.width / 2
    }px`,
    "--proplabelquiz-image-bottom": `${
      measuredImageBounds.top + measuredImageBounds.height
    }px`,
  } as CSSProperties;
  const imageStyle = {
    "--proplabelquiz-image-stage-left": `${imageStageBounds.left}px`,
    "--proplabelquiz-image-stage-top": `${imageStageBounds.top}px`,
    "--proplabelquiz-image-stage-width": `${imageStageBounds.width}px`,
    "--proplabelquiz-image-stage-height": `${imageStageBounds.height}px`,
  } as CSSProperties;
  const overlayDots = useMemo<PropLabelQuizAttachedLabelDot[]>(() => {
    if (
      !canRenderVisualImage ||
      !activeImageReady ||
      !activePreview
    ) {
      return [];
    }

    return activePreview.overlays.map((overlay, index) => ({
      id: `${overlay.clickableName}:${index}`,
      label: overlay.clickableName,
      left:
        imageBounds.left +
        clampNormalizedCoordinate(overlay.position.x) * imageBounds.width,
      top:
        imageBounds.top +
        clampNormalizedCoordinate(overlay.position.y) * imageBounds.height,
    }));
  }, [activeImageReady, activePreview, canRenderVisualImage, imageBounds]);
  const selectedOverlayDot = overlayDots.find(
    (dot) => Boolean(answer.tag) && dot.label === answer.tag,
  );
  const selectedLabelPlacement = selectedOverlayDot
    ? getPropLabelQuizAttachedLabelPlacement(
        selectedOverlayDot,
        overlayDots,
        frameBounds,
      )
    : undefined;

  return (
    <div className="proplabelquiz-inspectpicker">
      <div className="proplabelquiz-inspectpicker-frame" ref={frameRef}>
        {canRenderVisualImage && activePreview ? (
          <>
            <img
              ref={imageRef}
              className="proplabelquiz-inspectpicker-image"
              src={activeThumbnailUri}
              alt={activePreview.displayName}
              draggable={false}
              style={imageStyle}
              onLoad={() => {
                updateImageBounds();
                setLoadedThumbnailKey(activeThumbnailKey);
              }}
              onDragStart={(event) => event.preventDefault()}
            />
            <div
              className={classNames("proplabelquiz-inspectpicker-overlay", {
                "proplabelquiz-inspectpicker-overlay-ready": activeImageReady,
              })}
            >
              {activePreview.overlays.map((overlay, index) => {
                const selected =
                  Boolean(answer.tag) && answer.tag === overlay.clickableName;
                const dot = overlayDots[index];
                if (!dot) return null;

                return (
                  <button
                    key={`${overlay.clickableName}:${index}`}
                    type="button"
                    aria-label={`Select ${overlay.clickableName}`}
                    title={overlay.clickableName}
                    className={classNames("proplabelquiz-inspectpicker-dot", {
                      "proplabelquiz-inspectpicker-dot-muted": !selected,
                      "proplabelquiz-inspectpicker-dot-selected": selected,
                      "proplabelquiz-inspectpicker-dot-correct":
                        selected && answer.correct,
                    })}
                    style={{
                      left: `${dot.left}px`,
                      top: `${dot.top}px`,
                    }}
                    onClick={() =>
                      onSelectOverlay(activePreview.id, overlay.clickableName)
                    }
                  />
                );
              })}
              {selectedOverlayDot && selectedLabelPlacement && (
                <>
                  <div
                    className={classNames(
                      "proplabelquiz-inspectpicker-selected-label-connector",
                      {
                        "proplabelquiz-inspectpicker-selected-label-correct":
                          answer.correct,
                      },
                    )}
                    style={{
                      left: `${selectedLabelPlacement.connectorLeft}px`,
                      top: `${selectedLabelPlacement.connectorTop}px`,
                      width: `${selectedLabelPlacement.connectorWidth}px`,
                      transform: `translateY(-50%) rotate(${
                        selectedLabelPlacement.connectorRotation
                      }deg)`,
                    }}
                  />
                  <div
                    className={classNames(
                      "proplabelquiz-inspectpicker-selected-label",
                      {
                        "proplabelquiz-inspectpicker-selected-label-correct":
                          answer.correct,
                      },
                    )}
                    data-testid="prop-label-quiz-selected-dot-label"
                    style={{
                      left: `${selectedLabelPlacement.left}px`,
                      top: `${selectedLabelPlacement.top}px`,
                      width: `${selectedLabelPlacement.width}px`,
                      height: `${selectedLabelPlacement.height}px`,
                    }}
                  >
                    <span>{selectedOverlayDot.label}</span>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <div className="proplabelquiz-inspectpicker-empty">
            <PhotoLibraryOutlinedIcon fontSize="inherit" color="inherit" />
            <span>{fallbackLabel()}</span>
          </div>
        )}
        {frameControls && (
          <div
            className="proplabelquiz-inspectpicker-controls"
            style={frameControlsStyle}
          >
            {frameControls}
          </div>
        )}
        <div
          className="proplabelquiz-inspectpicker-cycle"
          style={frameControlsStyle}
        >
          <button
            type="button"
            className="proplabelquiz-inspectpicker-arrow proplabelquiz-inspectpicker-arrow-previous"
            aria-label="Previous inspect preview"
            disabled={previewItems.length < 2}
            onClick={() => cyclePreview(-1)}
          >
            <KeyboardArrowLeftIcon fontSize="inherit" color="inherit" />
          </button>
          <div className="proplabelquiz-inspectpicker-cycle-title">
            {activePreview?.displayName ?? "Inspect preview"}
          </div>
          <button
            type="button"
            className="proplabelquiz-inspectpicker-arrow proplabelquiz-inspectpicker-arrow-next"
            aria-label="Next inspect preview"
            disabled={previewItems.length < 2}
            onClick={() => cyclePreview(1)}
          >
            <KeyboardArrowRightIcon fontSize="inherit" color="inherit" />
          </button>
        </div>
      </div>

    </div>
  );
}
